import * as React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { io, Socket } from 'socket.io-client';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Navigator from '../components/page/Navigator';
import Header from '../components/page/Header';
import { AppState } from '../model/reducers';
import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import { withStyles } from '../settings/styles';
import defaultTheme, { IBaseTheme } from '../settings/themes';
import Axios from 'axios';
import LoginAPI from '../api/login';
import { NavigationGroup, NavigationPath, NavigationPaths, NavigationTab } from '../model/navigation/types';
import SubPages from './navigation/_index';
import { HashRouter as Router } from "react-router-dom";
import { broadcastMessage, closeDialog, endBroadcastMessage, loadingOverlay } from '../model/overlay/actions';
import { BroadcastState, BroadcastType, DialogState } from '../model/overlay/types';
import { Dialog, CircularProgress, AlertColor, Snackbar, SnackbarCloseReason, Alert } from '@mui/material';
import ViewerDialog from '../components/dialogs/ViewerDialog';
import { UserState } from '../model/user/types';
import { setActiveTab } from '../model/navigation/actions';
import { pagePaths, pageRedirects, pageTabs } from '../settings/pages';
const pjson = require('../../package.json');
const schedule = require('node-schedule');
const drawerWidth = 256;

// ----------------- State & Props -----------------  //

interface State
{
    branchName: string;
    mobileOpen: boolean;
    subPage: string;
    tabPage: number;
    urlHash: string;
};

interface LocalProps 
{
    mediaQuery?: boolean;
}

interface StateProps
{
    user: UserState;
    links: NavigationPaths;
    groups: NavigationGroup[];
    currentPage: string;
    reduxStoreLoaded: boolean;
    menuOpen: boolean;
    loading: boolean;
    broadcast: BroadcastState;
    dialog: DialogState;
    theme: IBaseTheme;
}

interface DispatchProps 
{
    setActiveTab: (pageId: string, tabId: string) => void;
    startBroadcast: (msg: string|JSX.Element, type?: BroadcastType) => void;
    endBroadcast: () => void;
    doLoading: () => void;
    doneLoading: () => void;
    closeDialog: typeof closeDialog;
}

type Props = LocalProps & StateProps & DispatchProps;

// -------------------- Class --------------------  //

class PortalPage extends React.Component<Props, State>
{
    // ============================================ //
    //               Member Variables               //
    // ============================================ //

    readonly sio: Socket;

    // ============================================ //
    //               Routine Functions              //
    // ============================================ //

    constructor(props: any)
    {
        super(props);

        // Init State
        this.state = {
            mobileOpen: false,
            branchName: pjson.build.branch,
            subPage: Object.keys(this.props.links)[0],
            tabPage: 0,
            urlHash: window.location.hash
        };

        this.sio = io(window.location.origin, {
            path: window.location.pathname+"socket.io"
        });
    }

    // ============================================ //

    render () 
    {
        const currentPage: NavigationPath = this.props.currentPage ? this.props.links[this.props.currentPage] : Object.values(this.props.links)[0];
        const currentTab: NavigationTab = pageTabs[currentPage.currentTab ?? currentPage.tabs[0]];

        const handleDrawerToggle = () => {
            this.setState({
                mobileOpen: !this.state.mobileOpen
            });
        };
        const tabClick = (tabId: string) => {
            this.props.setActiveTab(currentPage.id, tabId);
        }
        const onLogout = () => {
            this.props.doLoading();
            LoginAPI.logout().then(redirectUrl => {
                this.props.doneLoading();
                window.location.href = redirectUrl;
            });
        }
        const handleNavigation = (value: string) => {
            window.location.href = "#"+this.props.links[value].url;
        };
        const mobileProps = this.props.mediaQuery ? {} : {
            mobile: true,
            open: this.state.mobileOpen,
            onClose: handleDrawerToggle
        }

        return (<>
            <ThemeProvider<IBaseTheme> theme={this.props.theme}>
                <Router basename="/">
                    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                        <CssBaseline />
                        <Box
                            component="nav"
                            sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                        >
                            <Navigator
                                groups={this.props.groups}
                                pages={this.props.links}
                                currentPage={currentPage}
                                width={drawerWidth}
                                onChange={handleNavigation}
                                {...mobileProps}
                            />
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Header 
                                title={currentPage.title} 
                                icon={<currentPage.Icon />} 
                                tabs={currentPage.tabs.map(tabId=>pageTabs[tabId])} 
                                currentTab={currentTab}
                                loggedInUser={this.props.user.username}
                                onDrawerToggle={handleDrawerToggle} 
                                onTabClick={tabClick} 
                                onLogout={onLogout}
                            />
                            <Box component="main" sx={{ flex: 1, py: 6, px: 4, bgcolor: '#eaeff1' }}>
                                <SubPages pages={pagePaths} redirects={pageRedirects} tabs={pageTabs} />
                            </Box>
                        </Box>
                        {this.renderLoadingOverlay()}
                        {this.renderBroadcastOverlay()}
                        {this.renderDialogOverlay()}
                    </Box>
                </Router>
            </ThemeProvider>
        </>);
    }

    // ============================================ //

    componentDidMount()
    {
        this.fetchBranchName();
        this.setupServerEvents();
        this.setupContinuesAuthChecks();
    }

    // ============================================ //

    renderLoadingOverlay()
    {
        return (
            <Dialog 
                open={this.props.loading || !this.props.reduxStoreLoaded} 
                onBackdropClick={(evt)=>{evt.stopPropagation(); evt.preventDefault();}} 
                disableEscapeKeyDown 
                PaperProps={{style:{width: "100px", height: "100px"}}}
            >
                <CircularProgress style={{marginLeft: "30px", marginTop: "27px"}} />
            </Dialog>
        );
    }

    // ============================================ //

    renderBroadcastOverlay()
    {
        const { broadcast } = this.props;

        let type: AlertColor = 'info';
        if(broadcast.type !== undefined && broadcast.type !== null)
        switch(broadcast.type)
        {
            case BroadcastType.INFO:
                type = 'info';
                break;
            case BroadcastType.SUCCESS:
                type = 'success';
                break;
            case BroadcastType.WARNING:
                type = 'warning';
                break;
            case BroadcastType.ERROR:
                type = 'error';
                break;
        }

        return (<>
            <Snackbar open={this.props.broadcast.open} autoHideDuration={5000} onClose={(event: Event | React.SyntheticEvent, reason: SnackbarCloseReason)=>{
                if (reason === 'clickaway') return;
                this.props.endBroadcast();
            }}>
                <Alert onClose={this.props.endBroadcast} severity={type} variant="filled">
                    {this.props.broadcast.message}
                </Alert>
            </Snackbar>
        </>);
    }

    // ============================================ //

    renderDialogOverlay()
    {
        return (
            <ViewerDialog 
                open={this.props.dialog.open} 
                onClose={this.props.closeDialog}
            >
                {this.props.dialog.content}
            </ViewerDialog>
        )
    }
    
    // ============================================ //
    //               Member Functions               //
    // ============================================ //

    fetchBranchName()
    {
        if ( pjson.build &&
             pjson.build.branch === "")
        {
            Axios.get('./branch').then((response: any) => {
                this.setState({
                    branchName: response.data
                });
            }).catch((error: any) => {
                console.log(error);
            });
        }
    }

    // ============================================ //

    setupServerEvents()
    {
        this.sio.on('connect', ()=>{
            console.log("CONNECTED TO SOCKET");
        });

        this.sio.on('update', (event: any) => {
            if(event.info && event.info.api)
            {
                switch(event.info.api)
                {
                    default:
                        break;
                }
            }
        });
    }

    // ============================================ //

    setupContinuesAuthChecks()
    {
        schedule.scheduleJob('*/1 * * * *', function(fireDate: Date){
            LoginAPI.check().then((value: any)=>{
                window.location.href = value;
            })
        });
    }

    // ============================================ //
}

// ----------------- Export -----------------  //

const mapStateToProps = (state: AppState, props: LocalProps): StateProps => ({
    user: state.user.current,
    links: state.navigation.subPages,
    groups: state.navigation.groups,
    currentPage: state.navigation.currentSubPage,
    reduxStoreLoaded: (state.asyncInitialState as any).loaded,
    menuOpen: state.overlay.menuOpen,
    loading: state.overlay.loading,
    broadcast: state.overlay.broadcast,
    dialog: state.overlay.dialog,
    theme: defaultTheme
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{},{},any>, props: LocalProps): DispatchProps => ({
    setActiveTab: (pageId, tabId)=>dispatch(setActiveTab(pageId, tabId)),
    doLoading: ()=>dispatch(loadingOverlay(true)),
    doneLoading: ()=>dispatch(loadingOverlay(false)),
    startBroadcast: (message, type)=>dispatch(broadcastMessage(message, type)),
    endBroadcast: ()=>dispatch(endBroadcastMessage()),
    closeDialog: ()=>dispatch(closeDialog())
});

const withMediaQuery = (theme: any) => (Component: any) => (props: LocalProps) => {
    const mediaQuery = useMediaQuery(theme.breakpoints.up('sm'));
    return <Component {...props} mediaQuery={mediaQuery} />;
};

export default withStyles(connect(mapStateToProps, mapDispatchToProps)(withMediaQuery(defaultTheme)(PortalPage)));

// ------------------------------------------  //